import { useCallback, useMemo } from 'react';

import { useGetApiV1SsoimageStatuses } from '@/shared/api/generated/hooks/SSOImageController/useGetApiV1SsoimageStatuses';

export interface SlideStatusOption {
  readonly id: 1 | 2;
  readonly name: string;
  readonly tagVariant: 'helper' | 'positive';
}

/**
 * Hook que busca os status de slides via API e expõe utilitários de mapeamento.
 *
 * @returns Funções para obter label e variante de Tag a partir de um statusId.
 *
 * @example
 * ```tsx
 * const { getStatusLabel, getTagStatus } = useSlideStatuses();
 * <Tag label={getStatusLabel(slide.statusId)} status={getTagStatus(slide.statusId)} size="sm" />
 * ```
 */
export function useSlideStatuses() {
  const { data, isLoading } = useGetApiV1SsoimageStatuses();

  const statuses = useMemo<SlideStatusOption[]>(() => {
    if (!Array.isArray(data)) return [];
    return (data as { id: number; name: string }[]).map(s => ({
      id: s.id as 1 | 2,
      name: s.name,
      tagVariant: s.id === 2 ? 'positive' : ('helper' as 'helper' | 'positive'),
    }));
  }, [data]);

  const getStatusLabel = useCallback(
    (statusId?: 1 | 2): string => {
      return statuses.find(s => s.id === statusId)?.name ?? '';
    },
    [statuses],
  );

  const getTagStatus = useCallback((statusId?: 1 | 2): 'helper' | 'positive' => {
    return statusId === 2 ? 'positive' : 'helper';
  }, []);

  return { statuses, isLoading, getStatusLabel, getTagStatus } as const;
}
